import { Link } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/layout/Breadcrumbs";
import { FinalCTA } from "@/components/home/FinalCTA";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ArrowRight,
  CheckCircle2,
  ShieldCheck,
  BookOpen,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { posts } from "@/content/blog";
import { generateBreadcrumbSchema, organizationSchema, localBusinessSchema } from "@/lib/structuredData";

export interface PillarSection {
  title: string;
  body: string;
}

export interface PillarLink {
  label: string;
  href: string;
}

export interface PillarFAQ {
  q: string;
  a: string;
}

export interface PillarProps {
  eyebrow: string;
  title: string;
  intro: string;
  category: string; // matches blog category to surface related articles
  seoTitle: string;
  seoDescription: string;
  canonical: string;
  whatItIs: string;
  whoNeedsIt: string[];
  sections: PillarSection[];
  relatedServices: PillarLink[];
  faqs: PillarFAQ[];
  serviceSchema: object;
  icon: LucideIcon;
}

export function PillarTemplate(props: PillarProps) {
  const related = posts.filter((p) => p.category === props.category).slice(0, 6);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: props.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: "Home", url: "https://bookkeepingservices.co.za/" },
    { name: "Knowledge Centre", url: "https://bookkeepingservices.co.za/blog" },
    { name: props.title, url: `https://bookkeepingservices.co.za${props.canonical}` },
  ]);

  const Icon = props.icon;

  return (
    <Layout>
      <SEO
        title={props.seoTitle}
        description={props.seoDescription}
        canonical={props.canonical}
        structuredData={[organizationSchema, localBusinessSchema, props.serviceSchema, faqSchema, breadcrumbSchema]}
      />

      <Breadcrumbs
        items={[
          { name: "Knowledge Centre", href: "/blog" },
          { name: props.title },
        ]}
      />

      {/* Hero */}
      <section className="relative py-16 lg:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-accent/30" />
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            {props.eyebrow && (
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold uppercase tracking-wide mb-5">
                <Icon className="w-3.5 h-3.5" />
                {props.eyebrow}
              </div>
            )}
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-5">
              {props.title}
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed mb-8">{props.intro}</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/book-a-call">
                <Button size="lg" className="btn-primary gap-2 h-12">
                  Talk to a Qualified Accountant
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/packages">
                <Button size="lg" variant="outline" className="btn-outline-primary h-12">
                  View Packages
                </Button>
              </Link>
            </div>
            <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
              <ShieldCheck className="w-4 h-4 text-primary" />
              SAICA Registered · Reviewed by Chartered Accountants
            </div>
          </div>
        </div>
      </section>

      {/* What is it + Who needs it */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-10">
            <div className="bg-card border border-border rounded-2xl p-8">
              <h2 className="font-heading text-2xl font-bold mb-4">In a nutshell</h2>
              <p className="text-muted-foreground leading-relaxed">{props.whatItIs}</p>
            </div>
            <div className="bg-card border border-border rounded-2xl p-8">
              <h2 className="font-heading text-2xl font-bold mb-4">Who this applies to</h2>
              <ul className="space-y-2">
                {props.whoNeedsIt.map((w, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Pillar sections */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto space-y-12">
            {props.sections.map((s, i) => (
              <div key={i}>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground mb-4">
                  {s.title}
                </h2>
                <p className="text-muted-foreground leading-relaxed whitespace-pre-line">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related services */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold mb-8 text-center">
              Our related services
            </h2>
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {props.relatedServices.map((l) => (
                <Link
                  key={l.href}
                  to={l.href}
                  className="group bg-card border border-border rounded-xl p-5 hover:border-primary/40 hover:shadow-md transition-all flex items-center justify-between"
                >
                  <span className="font-semibold text-sm">{l.label}</span>
                  <ArrowRight className="w-4 h-4 text-primary group-hover:translate-x-1 transition-transform" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Knowledge cluster */}
      {related.length > 0 && (
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-5xl mx-auto">
              <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
                <div>
                  <div className="inline-flex items-center gap-2 text-primary text-sm font-semibold mb-2">
                    <BookOpen className="w-4 h-4" />
                    Knowledge cluster
                  </div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold">
                    More on {props.title.toLowerCase()}
                  </h2>
                </div>
                <Link to={`/blog/category/${props.category.toLowerCase()}`} className="text-primary text-sm font-semibold hover:underline">
                  View all {props.category} articles →
                </Link>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                {related.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/blog/${p.slug}`}
                    className="group bg-card border border-border rounded-xl p-5 hover:border-primary/40 hover:shadow-md transition-all"
                  >
                    <span className="inline-block px-2.5 py-0.5 rounded-full bg-primary/10 text-primary text-xs font-semibold mb-3">
                      {p.category}
                    </span>
                    <h3 className="font-heading font-bold mb-2 group-hover:text-primary transition-colors line-clamp-2">
                      {p.title}
                    </h3>
                    <p className="text-muted-foreground text-xs line-clamp-2">{p.description}</p>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-center mb-8">
              Frequently Asked Questions
            </h2>
            <Accordion type="single" collapsible className="space-y-3">
              {props.faqs.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`f-${i}`}
                  className="bg-card border border-border rounded-xl px-6"
                >
                  <AccordionTrigger className="text-left font-heading text-base font-semibold py-5 hover:no-underline hover:text-primary">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-5 leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <FinalCTA />
    </Layout>
  );
}
